import React, { useState } from 'react';
import FamilyMemberForm from './FamilyMemberForm';

const inputClass = "w-full p-3 bg-white border border-slate-300 rounded-xl text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent";
const labelClass = "block text-slate-700 text-sm font-medium mb-1.5";
const checkboxClass = "mt-1 min-w-[16px] min-h-[16px] w-4 h-4 text-blue-600 rounded focus:ring-blue-500";

function formatDateForInput(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  } catch { return ''; }
}

function mapFamilyForForm(family) {
  if (!family || !Array.isArray(family)) return [];
  return family.map(fm => ({
    name: fm.name || '',
    relation: fm.relation || '',
    gender: fm.gender || '',
    date_of_birth: formatDateForInput(fm.date_of_birth || fm.dob),
    sameHousehold: fm.sameHousehold || fm.same_household || false
  }));
}

const NewMemberForm = ({ onCreateMember, onCancel }) => {
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '', address: '', postcode: '', reason: '', family: [],
    dateOfBirth: '', preferredContactMethods: [], maritalStatus: '',
    agreedTerms: false, declaredTruthful: false, gdprConsent: false, contactAuthorisation: false
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleContactMethodChange = (method) => {
    const current = formData.preferredContactMethods || [];
    const updated = current.includes(method)
      ? current.filter(m => m !== method)
      : [...current, method];
    setFormData({ ...formData, preferredContactMethods: updated });
  };

  const handleFamilyChange = (index, e) => {
    const newFamily = [...formData.family];
    newFamily[index][e.target.name] = e.target.value;
    setFormData({ ...formData, family: newFamily });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreateMember(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <h2 className="text-2xl font-bold text-slate-900 mb-4">Add New Member</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>First Name *</label>
          <input type="text" name="firstName" value={formData.firstName} onChange={handleChange}
            className={inputClass} required maxLength={50} />
        </div>
        <div>
          <label className={labelClass}>Last Name *</label>
          <input type="text" name="lastName" value={formData.lastName} onChange={handleChange}
            className={inputClass} required maxLength={50} />
        </div>
        <div>
          <label className={labelClass}>Email *</label>
          <input type="email" name="email" value={formData.email} onChange={handleChange}
            className={inputClass} required maxLength={100} />
        </div>
        <div>
          <label className={labelClass}>Phone</label>
          <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
            className={inputClass} maxLength={20} placeholder="+44 7000 000000" />
        </div>
        <div>
          <label className={labelClass}>Date of Birth</label>
          <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange}
            className={inputClass} max={new Date().toISOString().split('T')[0]} />
        </div>
        <div>
          <label className={labelClass}>Marital Status</label>
          <select name="maritalStatus" value={formData.maritalStatus} onChange={handleChange} className={inputClass}>
            <option value="">Select status</option>
            <option value="unmarried">Unmarried</option>
            <option value="married">Married</option>
          </select>
        </div>
      </div>
      <div>
        <label className={labelClass}>Preferred Contact Methods</label>
        <div className="flex gap-4">
          {['sms', 'email', 'phone'].map(method => (
            <label key={method} className="flex items-center text-slate-600 gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.preferredContactMethods.includes(method)}
                onChange={() => handleContactMethodChange(method)}
                className={checkboxClass} />
              {method.charAt(0).toUpperCase() + method.slice(1)}
            </label>
          ))}
        </div>
      </div>
      <div>
        <label className={labelClass}>Address</label>
        <input type="text" name="address" value={formData.address} onChange={handleChange}
          className={inputClass} maxLength={200} />
      </div>
      <div>
        <label className={labelClass}>Postcode</label>
        <input type="text" name="postcode" value={formData.postcode} onChange={handleChange}
          className={inputClass} maxLength={10} placeholder="NN1 1AA" />
      </div>
      <div>
        <label className={labelClass}>Reason for Joining</label>
        <textarea name="reason" value={formData.reason} onChange={handleChange}
          className={inputClass} rows="3" maxLength={500} />
        <p className="text-xs text-slate-400 mt-1 text-right">{formData.reason.length}/500</p>
      </div>
      <FamilyMemberForm
        family={formData.family}
        onFamilyChange={handleFamilyChange}
        onAdd={() => setFormData({ ...formData, family: [...formData.family, { name: '', relation: '', gender: '', date_of_birth: '', sameHousehold: false }] })}
        onRemove={(i) => setFormData({ ...formData, family: formData.family.filter((_, idx) => idx !== i) })}
      />
      <div className="border border-slate-200 rounded-xl p-5 bg-slate-50 space-y-3">
        <h3 className="text-base font-semibold text-slate-800 mb-1">Declaration & Consent</h3>
        {[
          { key: 'agreedTerms', label: 'I agree to the Terms and Conditions of membership.' },
          { key: 'declaredTruthful', label: 'I declare that all information provided is truthful and accurate.' },
          { key: 'gdprConsent', label: 'I consent to the storage and processing of my personal data in accordance with GDPR regulations.' },
          { key: 'contactAuthorisation', label: 'I authorise the organisation to contact me with updates and relevant communications.' },
        ].map(({ key, label }) => (
          <label key={key} className="flex items-start text-slate-600 gap-3 cursor-pointer">
            <input type="checkbox" checked={formData[key]}
              onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })}
              className={checkboxClass} required />
            <span className="text-sm">{label}</span>
          </label>
        ))}
        <p className="text-slate-400 text-xs">Date of Declaration: {new Date().toLocaleDateString('en-GB')}</p>
      </div>
      <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
        <button type="button" onClick={onCancel} className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-all duration-200">Cancel</button>
        <button type="submit" className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl transition-all duration-200">Submit Application</button>
      </div>
    </form>
  );
};

const MemberEditForm = ({ member, onUpdateMember, onCancel }) => {
  const [formData, setFormData] = useState({
    firstName: member.name.split(' ')[0] || '',
    lastName: member.name.split(' ').slice(1).join(' ') || '',
    email: member.email || '',
    phone: member.phone || '',
    address: member.address || '',
    postcode: member.postcode || '',
    contribution: member.contribution || 0,
    paymentMethod: member.paymentMethod || '',
    joinDate: member.joinDate || '',
    family: mapFamilyForForm(member.family),
    dateOfBirth: formatDateForInput(member.date_of_birth),
    preferredContactMethods: member.preferred_contact_methods || [],
    maritalStatus: member.marital_status || '',
    contributionFrequency: member.contributionFrequency || 'monthly',
    agreedTerms: member.compliance?.agreedTerms || false,
    declaredTruthful: member.compliance?.declaredTruthful || false,
    gdprConsent: member.compliance?.gdprConsent || false,
    contactAuthorisation: member.compliance?.contactAuthorisation || false
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleContactMethodChange = (method) => {
    const current = formData.preferredContactMethods || [];
    const updated = current.includes(method)
      ? current.filter(m => m !== method)
      : [...current, method];
    setFormData({ ...formData, preferredContactMethods: updated });
  };

  const handleFamilyChange = (index, e) => {
    const newFamily = [...formData.family];
    newFamily[index][e.target.name] = e.target.value;
    setFormData({ ...formData, family: newFamily });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdateMember(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <h2 className="text-2xl font-bold text-slate-900 mb-4">Edit: {member.name}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>First Name *</label>
          <input type="text" name="firstName" value={formData.firstName} onChange={handleChange}
            className={inputClass} required maxLength={50} />
        </div>
        <div>
          <label className={labelClass}>Last Name *</label>
          <input type="text" name="lastName" value={formData.lastName} onChange={handleChange}
            className={inputClass} required maxLength={50} />
        </div>
        <div>
          <label className={labelClass}>Email *</label>
          <input type="email" name="email" value={formData.email} onChange={handleChange}
            className={inputClass} required maxLength={100} />
        </div>
        <div>
          <label className={labelClass}>Phone</label>
          <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
            className={inputClass} maxLength={20} placeholder="+44 7000 000000" />
        </div>
        <div>
          <label className={labelClass}>Date of Birth</label>
          <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange}
            className={inputClass} max={new Date().toISOString().split('T')[0]} />
        </div>
        <div>
          <label className={labelClass}>Marital Status</label>
          <select name="maritalStatus" value={formData.maritalStatus} onChange={handleChange} className={inputClass}>
            <option value="">Select status</option>
            <option value="unmarried">Unmarried</option>
            <option value="married">Married</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>Contribution (£)</label>
          <input type="number" name="contribution" value={formData.contribution} onChange={handleChange}
            className={inputClass} step="0.01" min="0" max="9999" />
        </div>
        <div>
          <label className={labelClass}>Payment Method</label>
          <select name="paymentMethod" value={formData.paymentMethod} onChange={handleChange} className={inputClass}>
            <option value="">Select method</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="standing_order">Standing Order</option>
            <option value="direct_debit">Direct Debit</option>
            <option value="cash">Cash</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>Contribution Frequency</label>
          <select name="contributionFrequency" value={formData.contributionFrequency} onChange={handleChange} className={inputClass}>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annually">Annually</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>Join Date</label>
          <input type="date" name="joinDate"
            value={formData.joinDate ? new Date(formData.joinDate).toISOString().split('T')[0] : ''}
            onChange={handleChange} className={inputClass} />
        </div>
      </div>
      <div>
        <label className={labelClass}>Preferred Contact Methods</label>
        <div className="flex gap-4">
          {['sms', 'email', 'phone'].map(method => (
            <label key={method} className="flex items-center text-slate-600 gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.preferredContactMethods.includes(method)}
                onChange={() => handleContactMethodChange(method)}
                className={checkboxClass} />
              {method.charAt(0).toUpperCase() + method.slice(1)}
            </label>
          ))}
        </div>
      </div>
      <div>
        <label className={labelClass}>Address</label>
        <input type="text" name="address" value={formData.address} onChange={handleChange}
          className={inputClass} maxLength={200} />
      </div>
      <div>
        <label className={labelClass}>Postcode</label>
        <input type="text" name="postcode" value={formData.postcode} onChange={handleChange}
          className={inputClass} maxLength={10} />
      </div>
      <FamilyMemberForm
        family={formData.family}
        onFamilyChange={handleFamilyChange}
        onAdd={() => setFormData({ ...formData, family: [...formData.family, { name: '', relation: '', gender: '', date_of_birth: '', sameHousehold: false }] })}
        onRemove={(i) => setFormData({ ...formData, family: formData.family.filter((_, idx) => idx !== i) })}
      />
      <div className="border border-slate-200 rounded-xl p-5 bg-slate-50 space-y-3">
        <h3 className="text-base font-semibold text-slate-800 mb-1">Declaration & Consent</h3>
        {!formData.agreedTerms && !formData.declaredTruthful && !formData.gdprConsent && !formData.contactAuthorisation && (
          <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm mb-2">
            ⚠ This member has not completed compliance declarations. Tick the boxes below after verifying their signed form.
          </p>
        )}
        {[
          { key: 'agreedTerms', label: 'Terms and Conditions agreed' },
          { key: 'declaredTruthful', label: 'Truthful information declaration' },
          { key: 'gdprConsent', label: 'GDPR data storage and processing consent' },
          { key: 'contactAuthorisation', label: 'Contact and updates authorisation' },
        ].map(({ key, label }) => (
          <label key={key} className="flex items-start text-slate-600 gap-3 cursor-pointer">
            <input type="checkbox" checked={formData[key]}
              onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })}
              className={checkboxClass} />
            <span className="text-sm">{label}</span>
          </label>
        ))}
        {member.compliance?.declarationDate && (
          <p className="text-slate-400 text-xs">Originally declared: {new Date(member.compliance.declarationDate).toLocaleDateString('en-GB')}</p>
        )}
      </div>
      <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
        <button type="button" onClick={onCancel} className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-all duration-200">Cancel</button>
        <button type="submit" className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl transition-all duration-200">Update</button>
      </div>
    </form>
  );
};

export { NewMemberForm, MemberEditForm };
export default NewMemberForm;
